import { Component, Inject, OnInit } from '@angular/core'
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog'
import { Channel, ChannelNameTokens, Destroyable, selectChannelById, untilDestroy } from '@tokeet-frontend/tv3-platform'
import { select, Store } from '@ngrx/store'
import * as fromRoot from '@tv3/store/state'
import { ChannelHelperService } from '@tv3/containers/channels/channel-connect/channel-helper.service'
import { map, switchMap, take } from 'rxjs/operators'
import {
  selectConnectionViewsByChannelId,
  selectListingErrorsByChannelId,
} from '@tv3/store/connection/connection.selectors'
import { ChannelListingError } from '@tokeet-frontend/channels'
import { ConnectionView, isDisabledStatusText } from '@tv3/store/connection/connection.view'
import { combineLatest, Observable, of } from 'rxjs'
import { Actions, ofType } from '@ngrx/effects'
import { CloseAllDialogs } from '@tokeet-frontend/tv3-platform'
import { UnlinkConnectionWithRentalComplete } from '@tv3/store/connection/connection.actions'
import * as R from 'ramda'
import { AirbnbSyncSettingsDialogService } from '../../channel-connect/dialogs/airbnb-sync-settings/airbnb-sync-settings-dialog.service'
import { ChannelConnectWizardsService } from '../../channel-connect/channel-connect-wizards/index.service'
import { BookingFormulasOverlayService } from '@tv3/containers/formulas/overlay/booking-formulas-overlay.service'
import { isChannelReviewAvailable } from '@tokeet-frontend/reviews'
import { SelectChannelAccountDialogService } from '@tv3/containers/channels/channel-connect/dialogs/select-channel-account/select-channel-account-dialog.service'
import { ChannelAccountsCacheService } from './channel-accounts-cache.service'
import { environment } from '@tv3/environments/environment'

export enum ChannelDetailsDialogTab {
  MappedRentals,
  Accounts,
  Commissions,
  Reviews,
  ABBOpportunities,
  ABBNotifications,
  Formulas,
  TwoWaySync,
}

@Component({
  selector: 'app-api-channel-dialog',
  templateUrl: './api-channel-dialog.component.html',
  styleUrls: ['./api-channel-dialog.component.scss'],
})
export class ApiChannelDialogComponent extends Destroyable implements OnInit {
  channel: Channel
  connections: ConnectionView[] = []
  tabs = ChannelDetailsDialogTab
  currentTab = ChannelDetailsDialogTab.MappedRentals

  nameTokens = ChannelNameTokens

  // TV4-5830: gate the "Simulate connection error" QA helper to non-production environments.
  // NOTE: the `production` flag is true on staging too (AOT prod build), so we key off the
  // `environment` name instead. Hides on prod AND preprod.
  isNonProd = environment.environment !== 'production'

  isReviewAvailable = false

  hasDisabled$: Observable<boolean>
  hasDisconnectedAccounts$: Observable<boolean>
  affectedRentalsCount$: Observable<number>
  roomMissingCount$: Observable<number>
  unlinkedListingsCount$: Observable<number>
  listingErrors$: Observable<ChannelListingError[]>
  tripListingRetryText = $localize`Try again shortly, or check the property in the Trip.com extranet.`

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: { channel: Channel },
    private store: Store<fromRoot.State>,
    private actions: Actions,
    private channelConnectWizards: ChannelConnectWizardsService,
    private channelHelperService: ChannelHelperService,
    private bookingFormulasOverlay: BookingFormulasOverlayService,
    public dialogRef: MatDialogRef<ApiChannelDialogComponent>,
    private airbnbSyncSettingsDialog: AirbnbSyncSettingsDialogService,
    private selectChannelAccountDialog: SelectChannelAccountDialogService,
    private accountsCache: ChannelAccountsCacheService
  ) {
    super()
  }

  ngOnInit() {
    this.listingErrors$ = this.store.pipe(select(selectListingErrorsByChannelId(this.data.channel.id)))
    this.store
      .pipe(select(selectChannelById, { id: this.data.channel.id }), untilDestroy(this))
      .subscribe((channel) => {
        this.channel = channel
        this.isReviewAvailable = !!isChannelReviewAvailable(this.channel.name)
      })

    const connections$ = this.store.pipe(select(selectConnectionViewsByChannelId, { id: this.data.channel.id }))

    connections$.pipe(untilDestroy(this)).subscribe((connections) => {
      this.connections = [...connections]
    })

    this.hasDisabled$ = connections$.pipe(
      map((connections) => R.any((c) => isDisabledStatusText(c.statusText), connections))
    )

    const accounts$ = this.accountsCache.getAccounts(this.data.channel)

    this.hasDisconnectedAccounts$ = accounts$.pipe(map((accounts) => (accounts || []).some((a) => a.status === 0)))

    // TV4-6062: mappings whose channel room no longer exists (deleted on the channel side).
    this.roomMissingCount$ = connections$.pipe(map((connections) => connections.filter((c) => c.roomMissing).length))

    // TV4-6008: listings unlinked from the API that have not been relinked yet.
    // Only tombstones whose room is still unmapped count -- a relink clears the
    // tombstone server-side, but the accounts cache may lag behind.
    this.unlinkedListingsCount$ = combineLatest([accounts$, connections$]).pipe(
      map(([accounts, connections]) => {
        const mappedRoomIds = new Set(connections.filter((c) => c.rentalId).map((c) => String(c.roomId)))
        const unlinkedIds = new Set<string>()
        for (const a of accounts || []) {
          for (const u of a.unlinked_listings || []) {
            if (u?.listing_id && !mappedRoomIds.has(String(u.listing_id))) {
              unlinkedIds.add(String(u.listing_id))
            }
          }
        }
        return unlinkedIds.size
      })
    )

    this.affectedRentalsCount$ = combineLatest([accounts$, connections$]).pipe(
      map(([accounts, connections]) => {
        const disabledPropertyIds = new Set(
          (accounts || []).filter((a) => a.status === 0).map((a) => String(a.propertyId))
        )
        const affectedRentalIds = new Set<string>()
        for (const c of connections) {
          if (c.rentalId && disabledPropertyIds.has(String(c.propertyId))) {
            affectedRentalIds.add(c.rentalId)
          }
        }
        return affectedRentalIds.size
      })
    )

    // TV4-6008: an unlink (single or bulk -- both complete with this action)
    // writes an unlinked-listing tombstone server-side; refresh the cached
    // accounts so the warnings reflect it without reopening the dialog.
    this.actions
      .pipe(ofType(UnlinkConnectionWithRentalComplete), untilDestroy(this))
      .subscribe(() => this.accountsCache.invalidate(this.data.channel))

    this.actions.pipe(ofType(CloseAllDialogs), untilDestroy(this)).subscribe(() => this.dialogRef.close())
  }

  close() {
    this.dialogRef.close()
  }

  onAddAccount() {
    this.channelConnectWizards.connect(this.channel).subscribe(() => {
      this.accountsCache.invalidate(this.channel)
    })
  }

  onConnect() {
    this.channelConnectWizards.connect(this.channel).subscribe(() => {
      this.accountsCache.invalidate(this.channel)
    })
  }

  onAirbnbSyncSettings() {
    this.airbnbSyncSettingsDialog.open(this.channel.id)
  }

  onDisconnect() {
    this.selectChannelAccountDialog
      .open({ channel: this.channel })
      .pipe(
        switchMap(({ account }) => this.channelHelperService.disconnectAccount(this.channel, account)),
        take(1)
      )
      .subscribe(() => {
        this.accountsCache.invalidate(this.channel)
      })
  }

  onBookingFormula() {
    this.bookingFormulasOverlay.open(this.channel)
  }

  // TV4-5830 (QA/testing helper, non-production only): force the Booking.com connection(s) into the
  // disabled / needs-reconnect state so the reconnect banner + notification cron can be exercised.
  onSimulateBookingError() {
    this.channelHelperService.simulateBookingError(this.channel).pipe(take(1)).subscribe()
  }
}
